import { useLang } from 'hooks/lang';

import { Button, Column, Label, Row } from 'components/Blocks';
import { MonetaryInput } from 'components/Inputs';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { calculateAvailableAmount, getExactDecimal } from './utils';
import { formatCurrency } from 'utils/text';

interface ProjectInvoiceValueState {
  currency?: string;
  value: string;
  country: string;
}

interface Props {
  state: ProjectInvoiceValueState;
  dispatch: (state: Partial<ProjectInvoiceValueState>) => void;
  requireCurrencyChange?: boolean;
  disabled?: boolean;
  currency?: string;
}

export function ProjectInvoiceValue({
  state,
  dispatch,
  requireCurrencyChange = false,
  disabled,
  currency,
}: Props): JSX.Element {
  const { t } = useLang();

  const { project } = useProjectProfile();

  const availableAmount = calculateAvailableAmount(project);

  const selectedValueIsOk = !!state.value && getExactDecimal(state.value) <= availableAmount;

  const defaultCurrency = currency || project.currency;

  return (
    <Column gap="2" fullWidth>
      <Label>{t('invoice-value')}</Label>

      <MonetaryInput
        error={disabled ? false : !selectedValueIsOk}
        currency={requireCurrencyChange ? state.currency || '' : defaultCurrency}
        value={state.value}
        currencyDisabled={!requireCurrencyChange || disabled}
        disabled={disabled}
        onChangeDirect={(value) => dispatch({ value })}
        onCurrencyChangeDirect={
          requireCurrencyChange
            ? (currencyValue) => dispatch({ currency: currencyValue })
            : undefined
        }
      />

      <Row className="text-xs text-destructive-light" justifyContent="flex-end" gap="1">
        <Button
          variant="link"
          className="text-xs text-inherit"
          onClick={() => dispatch({ value: availableAmount.toFixed(2) })}
          tooltipContent="Use this value"
        >
          {formatCurrency(availableAmount, project.currency)}
        </Button>

        {t('left-to-be-invoiced')}
      </Row>
    </Column>
  );
}
